// One order inside an expanded advisor play: who plays it, what card it is, and what it is aimed at.
// The row exists so the player can take the play apart before committing it — remove a card, or hand one
// back to the map for a new target — which is the whole of "vet" in "propose, vet, then act" (spec §6).

import { card, FAMILY_META } from '../../sim/deck';
import type { ValidOrder } from '../../ai/validate';
import { Chip, ShapeTile } from './ui';

export function PlayCard({
  order, callsign, targetSummary, testIdPrefix, retargeting = false, onRemove, onRetarget, retargetReason,
}: {
  order: ValidOrder;
  callsign: string;
  /** Where the card is aimed, already phrased for the rail: a grid reference, a graphic name, a callsign. */
  targetSummary: string;
  testIdPrefix: string;
  /** True while this card is out on the map waiting for its new target. */
  retargeting?: boolean;
  onRemove?: () => void;
  onRetarget?: () => void;
  /** Set when retargeting is unavailable (the planning stage has no map targeting); the chip dims. */
  retargetReason?: string;
}) {
  const def = card(order.card);
  const meta = FAMILY_META[def.family];
  return (
    <li
      data-testid={testIdPrefix}
      className="flex items-center gap-2 rounded-sm border px-1.5 py-1"
      style={{
        borderColor: retargeting ? meta.color : 'var(--color-slate-700)',
        background: 'var(--color-slate-800)',
      }}
    >
      <ShapeTile meta={meta} size={14} active={retargeting} solid />
      <span className="min-w-0 flex-1">
        <span className="mono block truncate text-[10px] tracking-[0.08em] text-paper">
          {callsign} · {def.label}
        </span>
        <span className="mono block truncate text-[9px] text-fog">{targetSummary}</span>
      </span>
      {onRetarget && (
        <Chip
          onClick={retargetReason ? undefined : onRetarget}
          disabled={retargetReason !== undefined}
          active={retargeting}
          testId={`${testIdPrefix}-retarget`}
          title={retargetReason ?? 'Hand this card to the map: pick a new target and it goes out on its own'}
        >
          {retargeting ? 'pick target' : 'retarget'}
        </Chip>
      )}
      {onRemove && (
        <button
          type="button"
          data-testid={`${testIdPrefix}-remove`}
          onClick={onRemove}
          title="Drop this card from the play"
          className="mono shrink-0 rounded-sm border border-slate-700 px-1 py-0.5 text-[10px] leading-none text-fog"
        >
          ✕
        </button>
      )}
    </li>
  );
}
